<template>
  <RouterView v-if="loaded" v-slot="{ Component }">
    <Transition
      mode="out-in"
      :name="$route.meta.transition || 'v-fade-fast'"
      @after-leave="onAfterRouteLeave"
      @before-leave="onBeforeRouteLeave"
      @leave-cancelled="onAfterRouteLeave"
    >
      <Component :is="Component" />
    </Transition>
  </RouterView>
</template>

<script setup lang="ts">
import { IndexedDBCache } from '~/services/cache/IndexedDBCache.ts'
import { checkCurrentRoute, createRouterGuards, onAfterRouteChange, onAfterRouteLeave, onBeforeRouteLeave } from './router-helpers.ts'
import { config } from '@vartion/ui'

const loaded = ref(false)

async function onCreated() {
  if (isOneTimeCase()) {
    await $router.replace('/one-time-case')
  }

  try {
    // Try to load the user if it is already authenticated.
    await Promise.all([stores.initialState.load(), window.localePromise])
    if ($env.app_env === 'testing') {
      config.VButton.debounce = false
    }
    await stores.user.load()
    await stores.organization.load()
    await stores.settings.load()
  } catch {
    // If we're not authenticated, redirect to the login page.
    if (!$route.value.meta.guest) {
      if ($route.value.fullPath !== '/') {
        await $router.replace({ path: '/login', query: { next: $route.value.fullPath } })
      } else {
        await $router.replace('/login')
      }
    }
  }

  // Azure sign in redirects back here. We check if we had an intended route to go to.
  // Redirect to that route now that we are authenticated.
  const next = await IndexedDBCache.tags('router').get('next')
  if (next) {
    await $router.replace(next)
    IndexedDBCache.tags('router').flush()
  }

  // After the user is loaded or redirected to the login page, we can start the permission and organizationId watchers.
  watch(
    () => $route.value.meta.permission,
    (permission) => {
      // If the route has 'administrations.view' permission attached, it means it is a spectrum route.
      app.config.globalProperties.$inAdministration = !!permission?.startsWith('administrations.')
    },
    { immediate: true },
  )

  // If the route `organizationId` param changes, try to switch to that organization.
  watch(
    () => $route.value.params.organizationId,
    async (organizationId) => {
      if (stores.user.authenticated && organizationId) {
        const id = Number(organizationId)
        await IndexedDBCache.tags('organization').put(stores.user.user.id, id)
        stores.organization.switch(id)
      }
    },
    { immediate: true },
  )

  // We can now create the router guards.
  // These prevent us from going to pages we are not supposed to access.
  createRouterGuards()
  await checkCurrentRoute()

  // Finally show the page.
  loaded.value = true

  onAfterRouteChange($route.value, $route.value)
}

// Wait for vue router to be ready, otherwise $route.value will not be correct in onCreated.
$router.isReady().then(() => onCreated())
</script>
